import { CentredFlexRow } from '@fox/layout/src/flex';
import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import { forwardRef, useState } from 'react';
import styled from 'styled-components';
import { CircularProgress } from '../circular-progess/index';
import { Icon, IconName } from '../icon/index';

type ButtonPaletteKey = Exclude<NonNullable<MuiButtonProps['color']>, 'inherit'>;

export interface ButtonProps extends Omit<MuiButtonProps, 'variant'> {
	/** The name of the startIcon, use this instead of passing an actual element to the startIcon prop. */
	startIconName?: IconName;

	/** The name of the endIcon, use this instead of passing an actual element to the endIcon prop. */
	endIconName?: IconName;

	/** When true will disable the button and show a loading spinner. */
	loading?: boolean;

	/** The variant of the button, see the 'Heirarchy' section of the Button documentation for an outline of the different use cases. */
	variant?: 'primary' | 'secondary' | 'tertiary';

	target?: React.HTMLAttributeAnchorTarget;
}

const StyledButton = styled(MuiButton)<{ $colorKey: ButtonPaletteKey }>`
	position: relative;
	border-radius: 12px;
	font-weight: 700;
	font-size: 0.9375rem;
	line-height: 26px;
	letter-spacing: 0.46px;
	text-transform: none;

	&.MuiButton-sizeSmall {
		height: 36px;
		padding: 6px 16px;
		line-height: 24px;
		letter-spacing: 0.4px;
	}

	&.MuiButton-sizeMedium {
		height: 42px;
		padding: 8px 22px;
	}

	&.MuiButton-contained {
		box-shadow: none;
		color: ${({ theme }) => theme.palette.common.white_states.main};
		border: 1px solid ${({ theme, $colorKey }) => theme.palette[$colorKey]?._states?.outlinedBorder ?? 'transparent'};
		&:hover {
			box-shadow: none;
		}
		&.Mui-disabled {
			border-color: transparent;
		}
	}

	&.MuiButton-outlined {
		color: ${({ theme, $colorKey }) => theme.palette[$colorKey]?.main ?? 'inherit'};
		background-color: ${({ theme, $colorKey }) => {
			const states = theme.palette[$colorKey]?._states;
			if (states?.fill) return states.fill;
			if (theme.palette.mode === 'dark') return states?.hover ?? 'transparent';
			return 'transparent';
		}};
		border-color: ${({ theme, $colorKey }) => theme.palette[$colorKey]?._states?.outlinedBorder ?? 'transparent'};
		&:hover {
			background-color: ${({ theme, $colorKey }) => {
				const palette = theme.palette[$colorKey];
				if (!palette) return 'transparent';
				if (theme.palette.mode === 'dark') return palette._states?.hover ?? 'transparent';
				// Light mode: semantic colors have very subtle _states.hover, use palette.light instead
				const semanticColors = ['error', 'warning', 'info', 'success'];
				return semanticColors.includes($colorKey) ? palette.light : palette._states?.hover;
			}};
			${({ theme, $colorKey }) => {
				if ($colorKey !== 'primary' || theme.palette.mode === 'dark') return '';
				return `color: ${theme.palette.primary.contrastText};`;
			}}
		}
		&.Mui-disabled {
			border-color: ${({ theme }) => theme.palette._components?.input?.outlined?.enabledBorder ?? 'transparent'};
			background-color: transparent;
			color: ${({ theme }) => theme.palette.text.disabled};
		}
	}

	&.MuiButton-text {
		color: ${({ theme, $colorKey }) => theme.palette[$colorKey]?.main ?? 'inherit'};
		border: 1px solid transparent;
		&:hover {
			background-color: ${({ theme, $colorKey }) => theme.palette[$colorKey]?._states?.hover ?? 'transparent'};
			border-color: ${({ theme, $colorKey }) => theme.palette[$colorKey]?._states?.outlinedBorder ?? 'transparent'};
			${({ theme, $colorKey }) => {
				if ($colorKey !== 'primary' || theme.palette.mode === 'dark') return '';
				return `color: ${theme.palette.primary.contrastText};`;
			}}
		}
		&.Mui-disabled {
			color: ${({ theme }) => theme.palette.text.disabled};
		}
		&.MuiButton-sizeMedium {
			padding: 8px 11px;
		}
		&.MuiButton-sizeSmall {
			padding: 6px 8px;
		}
	}
`;

export const BUTTON_VARIANT_MAP = {
	primary: 'contained',
	secondary: 'outlined',
	tertiary: 'text'
} as const;

/**
 * Buttons are essential interactive elements that enable users to trigger actions. When incorporating buttons, it's important to think about
 * their visual hierarchy, semantic coloring and appropriate placement. Communication can be enhanced by including icons alongside text, but use
 * the IconButton component if only an icon is required.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
	const { variant, loading, disabled, endIcon, endIconName, startIcon, startIconName, children, className, onClick, color, ...rest } = props;

	const iconStart = startIconName ? <Icon iconName={startIconName} /> : startIcon;
	const iconEnd = endIconName ? <Icon iconName={endIconName} /> : endIcon;
	const iconPosSpinner = (
		<CentredFlexRow style={{ width: '1.5rem', height: '1.5rem' }}>
			<CircularProgress size="1rem" />
		</CentredFlexRow>
	);

	const [asyncLoading, setAsyncLoading] = useState(false);

	return (
		<StyledButton
			ref={ref}
			{...rest}
			color={color}
			$colorKey={color && color !== 'inherit' ? color : 'primary'}
			onClick={
				onClick
					? async e => {
							// Call the onClick handler - during its load we set asyncLoading so we can display this.
							// If it isn't async, this will be true->false within the handler, so no visual effect
							try {
								setAsyncLoading(true);
								await Promise.resolve(onClick(e));
							} catch (e) {
								throw e;
							} finally {
								setAsyncLoading(false);
							}
						}
					: undefined
			}
			disabled={loading || asyncLoading || disabled}
			startIcon={(loading || asyncLoading) && iconStart ? iconPosSpinner : iconStart}
			endIcon={(loading || asyncLoading) && !iconStart && iconEnd ? iconPosSpinner : iconEnd}
			variant={variant ? BUTTON_VARIANT_MAP[variant] : undefined}
			className={className}
		>
			{children}
			{(loading || asyncLoading) && !iconStart && !iconEnd ? <CircularProgress size="1rem" style={{ position: 'absolute' }} /> : null}
		</StyledButton>
	);
});
